import React, {useState, useEffect, useRef} from 'react';
import {
  ScrollView,
  View,
  Image,
  ImageBackground,
  Text,
  Pressable,
} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {Submission} from '../../Models/PowerSyncModels/models';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {
  TemplateCategoryTypes,
  TemplateTypes,
} from '../../Constants/TemplateCategoryTypes';
import {Navigation} from '../../Constants/Navigation';
import RecordList from '../Shared/RecordList';
import ScreenButtons from '../Shared/ScreenButtons';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {Images} from '../../Constants/Images';
import {useIsFocused} from '@react-navigation/native';
import {
  CATEGORY,
  METADATA_KEYS,
  PERMISSIONS,
} from '../../Constants/AppConstants';
import {usePermission} from '../../Providers/PermissionProvider';
import {useRelation} from '../../Providers/SubmissionRelationProvider';
import {FilterTypes} from '../../Constants/General';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import {FilterValue} from '../Shared/SearchBox';

export declare type PeopleProps = {
  navigation: any;
};

const People = (props: PeopleProps): React.ReactElement => {
  const [reports, setReports] = useState<Submission[]>([]);
  const [filterValue, setFilterValue] = useState<FilterValue>({
    startDate: '',
    endDate: '',
    statuses: [],
    types: [],
    person: '',
    project: '',
    equipmentType: '',
    equipment: '',
    pendingInvestigation: false,
    investigationAttached: false,
  });

  const isFocused = useIsFocused();

  const {hasPermission} = usePermission();
  const {getRelatedSubmissions} = useRelation();

  const {templateCategories, people, templates, submissions, powersyncReady} = usePowerSyncData();

  const user = usePowerSyncUser();

  const unselectCommand = useRef({
    unselect: () => {},
    setUnselectCommand(func: () => void) {
      this.unselect = func;
    },
  });

  useEffect(() => {
    const fetchAsync = async () => {
      await fetchReports();
    };
    fetchAsync();
  }, [powersyncReady]);

  useEffect(() => {
    const fetchAsync = async () => {
      if (isFocused) {
        await fetchReports();
      }
    };
    fetchAsync();
  }, [isFocused]);

  if (!isFocused) return <></>;

  const fetchReports = async () => {
    let viewReport = hasPermission(PERMISSIONS.REPORT_PEOPLE_VIEW);

    let templateCategory = templateCategories
      .find(t => t.name === TemplateCategoryTypes.PEOPLE);
      
    let listTemplateIds = templateCategory?.listTemplateIds ?? [];

    let peopleTemplates = templates
      .filter(
        t =>
          listTemplateIds.includes(t._id)
      )
      .map(t => t.name.toLowerCase().trim());

    let subs = submissions.filter(
      s =>
        peopleTemplates.includes(
          s.templateVersion.name?.toLowerCase().trim() ?? '',
        ) &&
        (s.submissionStatus.name === SubmissionStatuses.DRAFT ||
          s.submissionStatus.name === SubmissionStatuses.SUBMITTED),
    );

    if (!viewReport) {
      //Special permissions for PM/Supervisor

      let userEmail = user.getUserInfo().email;
      let personLoggedIn = people.find(x => x.email == userEmail);

      if (personLoggedIn) {
        let submissionIds = await getRelatedSubmissions(personLoggedIn._id);
        subs = subs.filter(
          x => submissionIds.includes(x._id) || x.createdBy == userEmail,
        );
      }
    }

    setReports(subs);
  };

  async function recordPressed(recordId: string): Promise<void> {
    props.navigation.push(Navigation.DYNAMICFORM, {
      submissionId: recordId,
      showConfirmation: false,
    });
  }

  return (
    <ScrollView style={{backgroundColor: 'white'}}>
      <Pressable
        style={{
          //@ts-ignore
          cursor: 'auto',
        }}
        onPress={() => {
          unselectCommand.current.unselect();
        }}>
        <>
          <View>
            <ImageBackground
              source={Images.PEOPLE}
              resizeMode='cover'
              style={{height: 260}}>
              <Text style={ReportsStyleSheet.headerTitle}>People</Text>
            </ImageBackground>
          </View>
          <View style={{flexDirection: 'row'}}>
            <View style={{flex: 2, padding: 28}}>
              <RecordList
                records={reports}
                showDrafts={true}
                hasMetaData={true}
                category={CATEGORY.PEOPLE}
                metaDataKey_title={METADATA_KEYS.WORKERID}
                showFilters={true}
                showGroup={false}
                filters={[
                  {label: 'Date Range', type: FilterTypes.DATE_RANGE},
                  {label: 'Type', type: FilterTypes.REPORT_TYPE},
                  {label: 'Person', type: FilterTypes.PERSON},
                  {label: 'Project/Site', type: FilterTypes.PROJECT},
                ]}
                navigation={props.navigation}
                onRecordPressed={recordId => recordPressed(recordId)}
                setUnselectCommand={(func: () => void) => {
                  unselectCommand.current.setUnselectCommand(func);
                }}
                filterValue={filterValue}
                onChangeFilter={filterValue => setFilterValue(filterValue)}
              />
            </View>
            <View style={{flex: 1}}>
              <ScreenButtons
                navigation={props.navigation}
                category={TemplateCategoryTypes.PEOPLE}
                permissions={PERMISSIONS.REPORT_PEOPLE_CREATE}
              />
              <View style={CommonStyleSheet.bcsfFooter}>
                <Image
                  source={Images.BCSF}
                  style={CommonStyleSheet.bcsfFooterImage}
                />
                <Text style={CommonStyleSheet.bcsfFooterText}>
                  BC <Text style={{fontWeight: '700'}}>Forest Safety</Text>
                </Text>
              </View>
            </View>
          </View>
        </>
      </Pressable>
    </ScrollView>
  );
};

export default People;
